import React, { useState } from 'react';
import api from '../services/api';
import { X, Users, Globe, Lock, Info, Rocket, CheckCircle } from 'lucide-react';
import { Toast } from '../Toast';

interface AddGroupModalProps {
    accountId: number | string | null;
    onClose: () => void;
    onSuccess: () => void;
}

export const AddGroupModal: React.FC<AddGroupModalProps> = ({ accountId, onClose, onSuccess }) => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [visibility, setVisibility] = useState<'private' | 'public'>('private');
    const [username, setUsername] = useState('');

    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState<{ msg: string, type: 'success' | 'error' } | null>(null);

    const handleCreate = async () => {
        if (!accountId) {
            setToast({ msg: "Selecione uma conta primeiro!", type: 'error' });
            return;
        }
        if (!title) {
            setToast({ msg: "O título do grupo é obrigatório.", type: 'error' });
            return;
        }
        if (visibility === 'public' && !username) {
            setToast({ msg: "Grupos públicos precisam de um link/username.", type: 'error' });
            return;
        }

        setLoading(true);
        try {
            await api.post('/groups/create', {
                account_id: accountId,
                title: title,
                description: description,
                is_private: visibility === 'private',
                is_channel: false // Default to Group
            });

            onSuccess();
            onClose();
        } catch (e: any) {
            console.error(e);
            const errorMsg = e.response?.data?.detail || e.response?.data?.error || "Erro desconhecido ao criar grupo.";
            setToast({ msg: errorMsg, type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center',
            zIndex: 1000, backdropFilter: 'blur(8px)'
        }}>
            {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

            <div className="modal-content-premium" style={{
                background: '#161618',
                borderRadius: '24px',
                width: '550px',
                maxWidth: '95%',
                border: '1px solid #333',
                color: 'white',
                boxShadow: '0 40px 80px rgba(0, 0, 0, 0.7)',
                display: 'flex', flexDirection: 'column',
                overflow: 'hidden'
            }}>
                {/* Header */}
                <div style={{
                    padding: '24px 32px',
                    borderBottom: '1px solid #282828',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    background: '#1a1a1c'
                }}>
                    <div>
                        <h2 style={{ fontSize: '20px', fontWeight: 600, margin: 0, letterSpacing: '-0.5px' }}>
                            Criar Grupo no Telegram
                        </h2>
                        <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                            Conecte seu bot aos clientes
                        </div>
                    </div>
                    <button onClick={onClose} style={{
                        background: '#222', border: 'none', color: '#fff',
                        cursor: 'pointer', padding: '8px', borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: '0.2s'
                    }}>
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div style={{ padding: '32px' }}>

                    {/* Título */}
                    <div style={{ marginBottom: '24px' }}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#888', marginBottom: '8px', letterSpacing: '1px' }}>TÍTULO DO GRUPO</label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type="text"
                                style={{
                                    width: '100%',
                                    background: '#0a0a0a',
                                    border: '1px solid #333',
                                    borderRadius: '12px',
                                    padding: '16px 16px 16px 48px',
                                    color: '#fff',
                                    fontSize: '15px',
                                    outline: 'none',
                                    transition: 'border-color 0.2s',
                                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.5)'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#ff6a00'}
                                onBlur={(e) => e.target.style.borderColor = '#333'}
                                placeholder="Ex: Comunidade VIP"
                                value={title}
                                onChange={e => setTitle(e.target.value)}
                            />
                            <Users size={20} color="#ff6a00" style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)' }} />
                        </div>
                    </div>

                    {/* Descrição */}
                    <div style={{ marginBottom: '32px' }}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#888', marginBottom: '8px', letterSpacing: '1px' }}>DESCRIÇÃO (OPCIONAL)</label>
                        <textarea
                            style={{
                                width: '100%',
                                background: '#0a0a0a',
                                border: '1px solid #333',
                                borderRadius: '12px',
                                padding: '16px',
                                color: '#fff',
                                fontSize: '14px',
                                minHeight: '100px',
                                outline: 'none',
                                resize: 'vertical',
                                fontFamily: 'inherit',
                                boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.5)',
                                transition: 'border-color 0.2s'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#ff6a00'}
                            onBlur={(e) => e.target.style.borderColor = '#333'}
                            placeholder="Informações sobre o propósito do grupo..."
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                        />
                    </div>

                    {/* Visibilidade */}
                    <div style={{ marginBottom: '24px' }}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#888', marginBottom: '12px', letterSpacing: '1px' }}>VISIBILIDADE</label>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            {/* Option Private */}
                            <div
                                onClick={() => setVisibility('private')}
                                style={{
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '20px',
                                    borderRadius: '16px',
                                    border: visibility === 'private' ? '2px solid #ff6a00' : '1px solid #333',
                                    background: visibility === 'private' ? 'rgba(255, 106, 0, 0.05)' : '#0a0a0a',
                                    cursor: 'pointer', transition: 'all 0.2s',
                                    position: 'relative', overflow: 'hidden'
                                }}
                            >
                                <Lock size={24} color={visibility === 'private' ? '#ff6a00' : '#666'} />
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ color: visibility === 'private' ? '#fff' : '#eee', fontSize: '14px', fontWeight: 600 }}>Privado</div>
                                    <div style={{ color: '#666', fontSize: '11px', marginTop: '4px' }}>Apenas convites</div>
                                </div>
                                {visibility === 'private' && (
                                    <div style={{ position: 'absolute', top: 8, right: 8 }}>
                                        <CheckCircle size={16} color="#ff6a00" />
                                    </div>
                                )}
                            </div>

                            {/* Option Public */}
                            <div
                                onClick={() => setVisibility('public')}
                                style={{
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '20px',
                                    borderRadius: '16px',
                                    border: visibility === 'public' ? '2px solid #ff6a00' : '1px solid #333',
                                    background: visibility === 'public' ? 'rgba(255, 106, 0, 0.05)' : '#0a0a0a',
                                    cursor: 'pointer', transition: 'all 0.2s',
                                    position: 'relative'
                                }}
                            >
                                <Globe size={24} color={visibility === 'public' ? '#ff6a00' : '#666'} />
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ color: visibility === 'public' ? '#fff' : '#eee', fontSize: '14px', fontWeight: 600 }}>Público</div>
                                    <div style={{ color: '#666', fontSize: '11px', marginTop: '4px' }}>Aberto a todos</div>
                                </div>
                                {visibility === 'public' && (
                                    <div style={{ position: 'absolute', top: 8, right: 8 }}>
                                        <CheckCircle size={16} color="#ff6a00" />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Username Input (Condicional) */}
                    {visibility === 'public' && (
                        <div style={{ marginBottom: '20px', animation: 'fadeIn 0.3s' }}>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#888', marginBottom: '8px', letterSpacing: '1px' }}>LINK PÚBLICO</label>
                            <div style={{ position: 'relative' }}>
                                <span style={{ position: 'absolute', left: '16px', top: '16px', color: '#666', fontWeight: 500, fontSize: '15px' }}>t.me/</span>
                                <input
                                    type="text"
                                    style={{
                                        width: '100%',
                                        background: '#0a0a0a',
                                        border: '1px solid #333',
                                        borderRadius: '10px',
                                        padding: '16px 16px 16px 60px',
                                        color: '#fff',
                                        fontSize: '15px',
                                        outline: 'none',
                                        fontWeight: 500
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#ff6a00'}
                                    onBlur={(e) => e.target.style.borderColor = '#333'}
                                    placeholder="nomedogrupo"
                                    value={username}
                                    onChange={e => setUsername(e.target.value)}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    padding: '24px 32px',
                    borderTop: '1px solid #282828',
                    background: '#1a1a1c',
                    display: 'flex', justifyContent: 'flex-end', gap: '16px'
                }}>
                    <button onClick={onClose} style={{
                        background: 'transparent', border: '1px solid #444', color: '#fff',
                        padding: '12px 24px', borderRadius: '10px', cursor: 'pointer', fontWeight: 500,
                        fontSize: '14px', transition: '0.2s'
                    }}>
                        Cancelar
                    </button>
                    <button onClick={handleCreate} disabled={loading} style={{
                        background: 'var(--btn-gradient)',
                        border: 'none', color: '#000',
                        padding: '12px 32px', borderRadius: '10px', cursor: 'pointer', fontWeight: 700,
                        fontSize: '14px', display: 'flex', alignItems: 'center', gap: '10px',
                        boxShadow: 'none',
                        opacity: loading ? 0.7 : 1
                    }}>
                        {loading ? 'Criando...' : <><Rocket size={18} /> Criar Grupo</>}
                    </button>
                </div>
            </div>
        </div>
    );
};
